import type {CheckResult, DecisionSource, RulePurpose} from '../../store/features/CallRestrictions/callRestrictionsTypes';

const SOURCE_LABEL: Record<DecisionSource, string> = {
    global: 'Blocked for everyone by PhoneHQ',
    phonehq_default: 'Set by PhoneHQ',
    tenant: 'Set by PhoneHQ',
    company: 'Whole company',
    group: 'Group',
    person: 'Person',
    number: 'Number rule',
    price_limit: 'Price limit',
    emergency: 'Emergency number',
    none: '',
};

export interface CheckText {
    verdict: 'Allowed' | 'Blocked';
    rule: string;
    sees: string;
}

/**
 * The verdict box of "Check a number": which rule decided and what the
 * person would see. `who` is the caller's first name, or undefined for
 * "Anyone" (company rules only).
 */
export const checkText = (result: CheckResult, purpose: RulePurpose, who?: string): CheckText => {
    const {source, label} = result.decidedBy;
    const sourceLabel = SOURCE_LABEL[source];

    if (result.emergency) {
        return {verdict: 'Allowed', rule: 'Emergency number · Always allowed', sees: 'The call connects normally. No rule can block emergency numbers.'};
    }

    const rule = sourceLabel && label
        ? `${sourceLabel} · ${label}`
        : sourceLabel || label || (result.allowed ? 'No rule blocks this number' : 'Not authorized');

    if (result.allowed) {
        return {
            verdict: 'Allowed',
            rule,
            sees: purpose === 'FORWARD' ? 'Calls are forwarded normally.' : 'The call connects normally.',
        };
    }

    if (purpose === 'FORWARD') {
        return {verdict: 'Blocked', rule, sees: 'Forwarding to this number can’t be saved, and forwards already set to it are paused.'};
    }
    if (source === 'global' && !who) {
        return {verdict: 'Blocked', rule, sees: 'Nobody can call or forward to this number.'};
    }
    const person = who ?? 'The caller';
    if (source === 'price_limit') {
        return {verdict: 'Blocked', rule, sees: `${person} sees “This number isn’t authorized” with the price limit as the reason.`};
    }
    return {verdict: 'Blocked', rule, sees: `${person} sees “This number isn’t authorized”. A desk phone hears the announcement.`};
};

export const formatMoney = (value: number | null | undefined, currency: string): string =>
    value === null || value === undefined ? '' : `${value.toFixed(2)} ${currency}`.trim();
